import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Plus, Trash2, Save, Trash, Info } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { createCase, createPublicRevision, getCase } from "@/lib/api";

const DOC_TYPES = [
  { v: "firma_singola", l: "Firma singola" },
  { v: "piu_firme", l: "Più firme sullo stesso documento" },
  { v: "testamento_olografo", l: "Testamento olografo" },
  { v: "manoscritto_intero", l: "Intero manoscritto" },
  { v: "sigla", l: "Sigla / paraffe" },
  { v: "compilazione_manoscritta", l: "Compilazione manoscritta" },
  { v: "firma_bancaria", l: "Firma su documento bancario" },
  { v: "firma_grafometrica", l: "Firma grafometrica / digitale" },
];

const AVAILABILITY = [
  { v: "originale", l: "Originale disponibile" },
  { v: "scansione", l: "Scansione ad alta risoluzione" },
  { v: "fotocopia", l: "Fotocopia" },
  { v: "fotografia", l: "Fotografia" },
];

const STEPS = ["Ruolo", "Richiedente", "Documento", "Comparative", "Cronologia", "Strategia", "Conferma"];

// Wizard-side preview of per-document cards using the humanized labels.
// Note: at Wizard time we don't yet have B.1 assessments — we only preview
// role/support/date so the user can verify what will be sent to the engine.
// After submit, the same MultidocSummary component is used on the Dashboard
// with the real per-document assessments.
function WizardMultidocPreview({ questionedDocuments }) {
  if (!questionedDocuments || questionedDocuments.length < 2) return null;
  const humanizeLocal = (v) => {
    const map = {
      originale: "Originale", scansione_colori: "Scansione a colori",
      scansione_bn: "Scansione in bianco e nero",
      fotocopia_colori: "Fotocopia a colori", fotocopia_bn: "Fotocopia in bianco e nero",
      fotografia: "Fotografia", riproduzione_digitale: "Riproduzione digitale",
      existing_available: "Originale disponibile",
      existing_not_available: "Originale esistente ma non disponibile",
      existence_not_verified: "Esistenza dell'originale non verificata",
      not_existing: "Originale non esistente",
      principale: "Principale", secondario: "Secondario", accessorio: "Accessorio",
    };
    if (!v) return "—";
    return map[String(v).toLowerCase()] || v;
  };
  return (
    <div className="border hairline p-4 bg-white" data-testid="wizard-multidoc-preview">
      <div className="overline mb-3">Documenti in verifica ({questionedDocuments.length})</div>
      <div className="space-y-2">
        {questionedDocuments.map((qd, i) => (
          <div key={qd.id || `qd-preview-${i}`} className="border-l-2 border-navy pl-3 py-1" data-testid={`wizard-multidoc-card-${i}`}>
            <div className="flex items-baseline gap-3">
              <span className="font-mono text-xs text-slate-500">DOC #{i.toString().padStart(2, "0")}</span>
              <span className="text-xs uppercase tracking-wider text-slate-500">{humanizeLocal(qd.role)}</span>
            </div>
            <div className="font-serif text-base font-semibold text-navy">{qd.title || qd.document_type || "—"}</div>
            <div className="text-xs text-slate-500 mt-1">
              {qd.date_value && `${qd.date_value} · `}
              {humanizeLocal(qd.material_support)}
              {qd.original_status && ` · ${humanizeLocal(qd.original_status)}`}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs italic text-slate-500 mt-3">
        La valutazione per-documento (score, lavorabilità, assumibilità, servizio) sarà calcolata
        automaticamente dopo la generazione del fascicolo e visibile nella dashboard e nel PDF.
      </p>
    </div>
  );
}
const STORAGE_KEY = "perizia-ready-wizard-draft-v2";

// FASE E.1 P1 — stable ID generator for dynamic list items (comparatives,
// timeline events, clinical documents, ricoveri, questioned documents).
// Root cause of the recurring `insertBefore` crash: legacy drafts saved before
// the D.4 fix had items WITHOUT `id`, so React fell back to `<prefix>-legacy-${i}`
// keys — which still change on delete/reorder. We normalize legacy items ONCE
// at load time so the renderer can safely use `key={item.id}`.
function _mkId(prefix) {
  const rnd = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID().slice(0, 8)
    : Math.random().toString(36).slice(2, 10);
  return `${prefix}_${Date.now().toString(36)}_${rnd}`;
}
function _ensureIds(items, prefix) {
  if (!Array.isArray(items)) return items;
  return items.map((x) => (x && typeof x === "object" && !x.id)
    ? { ...x, id: _mkId(prefix) } : x);
}
const STEP_MINUTES = [1, 2, 2, 4, 2, 3, 1];

const EMPTY = {
  role: "privato",
  // FASE E.3 · TURNO 2A — explicit user segmentation (private/lawyer/professional).
  user_type: "",
  ident: {
    document_type: "", contestation_type: "firma",
    document_availability: "", original_holder: "",
    already_in_court: null, disconoscimento: null, phase: "stragiudiziale",
    contested_document_date: "",
    subject: { name: "", first_name: "", last_name: "", dob: "", deceased_date: "" },
    mediazione_obbligatoria: false,
    subject_dob: "",
    subject_age_years: null,
    laterality: "",
    uses_glasses: false,
    visual_issues: [],
    visual_notes: "",
    // Phase — align Doc #1 UI/schema with Doc #2+ (2026-02-22)
    title: "",
    role: "principale",
    material_support: "",
    original_status: "",
    disputed_signature_count: null,
    contains_handwritten_text: null,
    contains_handwritten_numbers: null,
    contains_initials: null,
    contains_full_signature: null,
    contains_multiple_signatures: null,
    contains_multiple_writers: null,
    contested_area_description: "",
    notes: "",
  },
  comps: { comparatives: [], period_covered_years: null, subject_condition_notes: "", already_verified_documents: false },
  timeline: [],
  strategy: {
    producing_party: "", against_party: "", signature_status: "",
    verificazione_requested: null, querela_falso: null, ctu_disposed: null, deadline_notes: "",
    disconoscimento_dichiarazione: "", disconoscimento_note: "",
  },
  clinical: { documents: [], conditions: [], ricoveri: [], notes: "" },
  contact: { contact_name: "", contact_email: "", contact_phone: "" },
  // NEW (2026-02-21): distinct role fields
  requester: {
    first_name: "", last_name: "", title: "", organization: "", email: "", phone: "",
  },
  assistedParty: {
    first_name: "", last_name: "", role: "", role_other: "",
  },
  caseShortDescription: "",
  // Phase A multi-document (2026-02-21) — additional questioned docs beyond identification
  questionedDocuments: [],
  step: 0,
};

// Roles + statuses for questioned documents
const QDOC_ROLES = [
  { v: "principale", l: "Principale" },
  { v: "secondario", l: "Secondario" },
  { v: "accessorio", l: "Accessorio" },
];
const QDOC_ORIGINAL_STATUS = [
  { v: "existing_available", l: "Originale esistente e disponibile" },
  { v: "existing_not_available", l: "Originale esistente ma non disponibile" },
  { v: "existence_not_verified", l: "Esistenza dell'originale non verificata" },
  { v: "not_existing", l: "Originale non esistente" },
  { v: "unknown", l: "Dato non noto" },
];
const QDOC_MATERIAL = [
  { v: "originale", l: "Originale" },
  { v: "scansione_colori", l: "Scansione a colori" },
  { v: "scansione_bn", l: "Scansione in bianco e nero" },
  { v: "fotocopia_colori", l: "Fotocopia a colori" },
  { v: "fotocopia_bn", l: "Fotocopia in bianco e nero" },
  { v: "fotografia", l: "Fotografia" },
  { v: "riproduzione_digitale", l: "Riproduzione digitale" },
  { v: "non_disponibile", l: "Non disponibile" },
  { v: "altro", l: "Altro" },
];

// Position options for assisted party
const ASSISTED_PARTY_ROLES = [
  { v: "attore", l: "Attore" },
  { v: "convenuto", l: "Convenuto" },
  { v: "ricorrente", l: "Ricorrente" },
  { v: "resistente", l: "Resistente" },
  { v: "appellante", l: "Appellante" },
  { v: "appellato", l: "Appellato" },
  { v: "opponente", l: "Opponente" },
  { v: "opposto", l: "Opposto" },
  { v: "querelante", l: "Querelante" },
  { v: "querelato", l: "Querelato" },
  { v: "parte_istante", l: "Parte istante" },
  { v: "parte_chiamata", l: "Parte chiamata" },
  { v: "erede", l: "Erede" },
  { v: "beneficiario", l: "Beneficiario" },
  { v: "contestatore", l: "Contestatore" },
  { v: "parte_producente", l: "Parte che produce il documento" },
  { v: "parte_opposta", l: "Parte contro cui il documento è prodotto" },
  { v: "non_ancora_definita", l: "Non ancora definita" },
  { v: "altro", l: "Altro" },
];

// Private-user role options
const PRIVATE_CASE_ROLES = [
  { v: "persona_scrivente", l: "Persona cui è attribuita la firma" },
  { v: "erede", l: "Erede" },
  { v: "beneficiario", l: "Beneficiario" },
  { v: "parte_contesta", l: "Parte che contesta il documento" },
  { v: "parte_sostiene", l: "Parte che sostiene l'autenticità" },
  { v: "altro", l: "Altro" },
];

// Legal professional titles
const LEGAL_TITLES = [
  { v: "Avv.", l: "Avv." },
  { v: "Dott.", l: "Dott." },
  { v: "Dott.ssa", l: "Dott.ssa" },
  { v: "Altro", l: "Altro" },
];

const SOURCE_MATERIALS = [
  { v: "originale", l: "Originale disponibile" },
  { v: "originale_non_acquisito", l: "Originale esistente ma non acquisito" },
  { v: "scansione_colori", l: "Scansione a colori" },
  { v: "scansione_bn", l: "Scansione in bianco e nero" },
  { v: "fotografia", l: "Fotografia" },
  { v: "fotocopia_colori", l: "Fotocopia a colori" },
  { v: "fotocopia_bn", l: "Fotocopia in bianco e nero" },
  { v: "stampa", l: "Stampa" },
  { v: "formato_sconosciuto", l: "Formato sconosciuto" },
];

const LATERALITY_OPTIONS = [
  { v: "destro", l: "Destrimane" },
  { v: "mancino", l: "Mancino" },
  { v: "ambidestro", l: "Ambidestro" },
  { v: "non_noto", l: "Non noto" },
];

const VISUAL_ISSUES_OPTIONS = [
  "cataratta", "glaucoma", "maculopatia", "miopia forte",
  "ipovisione", "cecità parziale", "altro",
];

const CLINICAL_DOC_TYPES = [
  { v: "cartella_clinica_completa", l: "Cartella clinica completa" },
  { v: "cartella_clinica_parziale", l: "Cartella clinica parziale" },
  { v: "lettera_dimissione", l: "Lettera di dimissione" },
  { v: "certificato_medico", l: "Certificato medico" },
  { v: "referto", l: "Referto specialistico" },
  { v: "visita_neurologica", l: "Visita neurologica" },
  { v: "visita_ortopedica", l: "Visita ortopedica" },
  { v: "visita_geriatrica", l: "Visita geriatrica" },
  { v: "visita_psichiatrica", l: "Visita psichiatrica" },
  { v: "visita_oculistica", l: "Visita oculistica" },
  { v: "esame_diagnostico", l: "Esame diagnostico" },
  { v: "piano_terapeutico", l: "Piano terapeutico" },
  { v: "farmacologica", l: "Documentazione farmacologica" },
  { v: "riabilitativa", l: "Documentazione riabilitativa" },
  { v: "medico_curante", l: "Certificazione medico curante" },
  { v: "altro", l: "Altro" },
];

// ==== Sezione 4 — Cronologia del caso (nuova struttura 2026-02-20) ====
const TIMELINE_MACROS = [
  { v: "documento_verifica", l: "Evento riguardante il documento in verifica" },
  { v: "processuale", l: "Evento processuale o stragiudiziale" },
  { v: "personale", l: "Evento personale o contestuale" },
  { v: "altro", l: "Altro" },
];

const TIMELINE_SUBTYPES = {
  documento_verifica: [
    { v: "redazione", l: "Redazione del documento" },
    { v: "sottoscrizione", l: "Sottoscrizione" },
    { v: "consegna", l: "Consegna del documento" },
    { v: "autenticazione", l: "Autenticazione" },
    { v: "registrazione", l: "Registrazione" },
    { v: "deposito", l: "Deposito" },
    { v: "produzione_giudizio", l: "Produzione in giudizio" },
    { v: "rinvenimento", l: "Rinvenimento del documento" },
    { v: "acquisizione_originale", l: "Acquisizione dell'originale" },
    { v: "acquisizione_copia", l: "Acquisizione di copia o scansione" },
    { v: "contestazione", l: "Contestazione del documento" },
    { v: "disconoscimento_sottoscrizione", l: "Disconoscimento della sottoscrizione" },
    { v: "contestazione_testamento", l: "Contestazione del testamento" },
    { v: "contestazione_scrittura", l: "Contestazione della scrittura" },
    { v: "altro", l: "Altro evento riguardante il documento" },
  ],
  processuale: [
    { v: "diffida", l: "Diffida" },
    { v: "avvio_mediazione", l: "Avvio mediazione" },
    { v: "incontro_mediazione", l: "Incontro di mediazione" },
    { v: "deposito_atto", l: "Deposito atto introduttivo" },
    { v: "costituzione_giudizio", l: "Costituzione in giudizio" },
    { v: "produzione_documentale", l: "Produzione documentale" },
    { v: "disconoscimento", l: "Disconoscimento" },
    { v: "istanza_verificazione", l: "Istanza di verificazione" },
    { v: "querela_falso", l: "Querela di falso" },
    { v: "nomina_ctu", l: "Nomina del CTU" },
    { v: "nomina_ctp", l: "Nomina del CTP" },
    { v: "inizio_operazioni", l: "Inizio operazioni peritali" },
    { v: "udienza", l: "Udienza" },
    { v: "deposito_ctu", l: "Deposito della CTU" },
    { v: "osservazioni_ctu", l: "Osservazioni alla CTU" },
    { v: "sentenza", l: "Sentenza" },
    { v: "altro", l: "Altro evento processuale o stragiudiziale" },
  ],
  personale: [
    { v: "trasferimento_residenza", l: "Trasferimento di residenza" },
    { v: "cambio_domicilio", l: "Cambio di domicilio" },
    { v: "soggiorno_temporaneo", l: "Soggiorno temporaneo" },
    { v: "permanenza_estero", l: "Permanenza all'estero" },
    { v: "struttura_non_sanitaria", l: "Ricovero in struttura residenziale non sanitaria" },
    { v: "ingresso_rsa", l: "Ingresso in RSA (fatto contestuale)" },
    { v: "detenzione", l: "Detenzione" },
    { v: "matrimonio", l: "Matrimonio" },
    { v: "separazione", l: "Separazione" },
    { v: "decesso_familiare", l: "Decesso di un familiare" },
    { v: "lutto", l: "Lutto" },
    { v: "cambio_lavoro", l: "Cambio di lavoro" },
    { v: "pensionamento", l: "Pensionamento" },
    { v: "trasferimento_lavorativo", l: "Trasferimento lavorativo" },
    { v: "compravendita_immobile", l: "Vendita o acquisto di immobile" },
    { v: "altro", l: "Altro evento personale o contestuale" },
  ],
  altro: [
    { v: "altro", l: "Altro (specifica)" },
  ],
};

const DATE_PRECISION_OPTIONS = [
  { v: "exact", l: "Data esatta" },
  { v: "month_year", l: "Mese e anno" },
  { v: "year_only", l: "Solo anno" },
  { v: "estimated", l: "Data stimata" },
  { v: "unknown", l: "Data non nota" },
];

const EVENT_SOURCES = [
  { v: "", l: "— Fonte (facoltativa) —" },
  { v: "documento", l: "Documento" },
  { v: "dichiarazione_utente", l: "Dichiarazione dell'utente" },
  { v: "atto_processuale", l: "Atto processuale" },
  { v: "corrispondenza", l: "Corrispondenza" },
  { v: "altro", l: "Altro" },
];

// Euristica per warning "sembra sanitario"
const SANITARY_KEYWORDS_STRONG = [
  "visita", "ricovero", "referto", "certificato medico", "ospedale",
  "fisioterapia", "neurolog", "ortoped", "cardiolog", "diagnosi",
  "terapia", "intervento", "tutore", "frattura", "trauma", "dimissione",
  "cartella clinica",
];

const CLINICAL_CONDITIONS = [
  "tremore", "Parkinson", "ictus", "demenza", "Alzheimer",
  "patologie neurologiche", "patologie psichiatriche", "deficit motori",
  "artrosi grave", "fratture", "interventi chirurgici", "ricoveri",
  "terapie farmacologiche rilevanti", "sedazione", "condizioni non conosciute",
];

export default function Wizard() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // FASE E.3 · TURNO 1 (A) — edit-mode: same case_id, new revision.
  const editCaseId = searchParams.get("case_id");
  const isEditMode = Boolean(editCaseId);
  const [step, setStep] = useState(0);
  const [role, setRole] = useState("privato");
  // FASE E.3 · TURNO 2A — explicit user segmentation (private/lawyer/professional).
  const [userType, setUserType] = useState("");
  // FASE E.3 · TURNO 2C.1 — sync legacy `role` from `user_type` so the
  // content pipeline (which still reads `case.role` for language/tone)
  // stays consistent with the single user_type selector.
  useEffect(() => {
    if (userType === "private") setRole("privato");
    else if (userType === "lawyer") setRole("avvocato");
    else if (userType === "professional") setRole("professionista");
  }, [userType]);
  const [ident, setIdent] = useState(EMPTY.ident);
  const [comps, setComps] = useState(EMPTY.comps);
  const [timeline, setTimeline] = useState([]);
  const [strategy, setStrategy] = useState(EMPTY.strategy);
  const [clinical, setClinical] = useState(EMPTY.clinical);
  const [contact, setContact] = useState(EMPTY.contact);
  const [requester, setRequester] = useState(EMPTY.requester);
  const [assistedParty, setAssistedParty] = useState(EMPTY.assistedParty);
  const [caseShortDescription, setCaseShortDescription] = useState("");
  const [questionedDocuments, setQuestionedDocuments] = useState([]);
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [art9Consent, setArt9Consent] = useState(false);

  const hasClinicalData = (
    (clinical.documents && clinical.documents.length > 0) ||
    (clinical.conditions && clinical.conditions.length > 0) ||
    (clinical.ricoveri && clinical.ricoveri.length > 0) ||
    (clinical.notes && clinical.notes.trim())
  );

  const canLeaveStep4 = !hasClinicalData || art9Consent;
  const [saving, setSaving] = useState(false);
  const [resumedAt, setResumedAt] = useState(null);
  const firstRender = useRef(true);

  // ------- Resume from localStorage on mount OR prefill from existing case -------
  useEffect(() => {
    // FASE E.3 · TURNO 1 (A) — if `?case_id=X` is present, load the
    // ACTIVE revision snapshot from the server and prefill the wizard
    // state. localStorage draft is ignored to avoid mixing a new draft
    // with the existing fascicolo.
    if (isEditMode) {
      (async () => {
        try {
          const c = await getCase(editCaseId);
          setRole(c.role || "privato");
          setIdent({ ...EMPTY.ident, ...(c.identification || {}) });
          setComps({
            ...EMPTY.comps, ...(c.comparatives || {}),
            comparatives: _ensureIds((c.comparatives?.comparatives || []), "cmp"),
          });
          setTimeline(_ensureIds(c.timeline || [], "ev"));
          setStrategy({ ...EMPTY.strategy, ...(c.legal_strategy || {}) });
          setClinical({
            ...EMPTY.clinical, ...(c.clinical || {}),
            documents: _ensureIds((c.clinical || {}).documents || [], "clindoc"),
            ricoveri: _ensureIds((c.clinical || {}).ricoveri || [], "ricov"),
          });
          setContact({
            contact_name: c.contact_name || "",
            contact_email: c.contact_email || "",
            contact_phone: c.contact_phone || "",
          });
          setRequester({ ...EMPTY.requester, ...(c.requester || {}) });
          setAssistedParty({ ...EMPTY.assistedParty, ...(c.assisted_party || {}) });
          setCaseShortDescription(c.case_short_description || "");
          // DOC #00 is the identification; additional questioned_documents
          // start at order >= 1 to preserve the contract.
          const qds = Array.isArray(c.questioned_documents) ? c.questioned_documents : [];
          setQuestionedDocuments(_ensureIds(
            qds.filter((qd) => (qd.order ?? 0) > 0).map((qd) => ({ ...qd })),
            "qd"
          ));
          setStep(0);
          toast.success("Fascicolo caricato — modifica i dati e salva per creare una nuova revisione");
        } catch (e) {
          toast.error("Impossibile caricare il fascicolo per la modifica");
          console.error(e);
        }
      })();
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.savedAt) {
          setRole(d.role || "privato");
          setIdent({ ...EMPTY.ident, ...(d.ident || {}) });
          setComps({ ...EMPTY.comps, ...(d.comps || {}),
            // Legacy migration: `spontaneous` → `_legacy_spontaneous` (audit only)
            // + FASE E.1 P1: assign stable id to every legacy item without one.
            comparatives: _ensureIds((d.comps?.comparatives || []).map((c) => {
              if (c.spontaneous !== undefined && c._legacy_spontaneous === undefined) {
                const { spontaneous, ...rest } = c;
                return { ...rest, _legacy_spontaneous: spontaneous };
              }
              return c;
            }), "cmp")
          });
          setTimeline(_ensureIds(d.timeline || [], "ev"));
          setStrategy({ ...EMPTY.strategy, ...(d.strategy || {}) });
          setClinical({
            ...EMPTY.clinical, ...(d.clinical || {}),
            documents: _ensureIds((d.clinical || {}).documents || [], "clindoc"),
            ricoveri: _ensureIds((d.clinical || {}).ricoveri || [], "ricov"),
          });
          setContact({ ...EMPTY.contact, ...(d.contact || {}) });
          setRequester({ ...EMPTY.requester, ...(d.requester || {}) });
          setAssistedParty({ ...EMPTY.assistedParty, ...(d.assistedParty || {}) });
          setCaseShortDescription(d.caseShortDescription || "");
          setQuestionedDocuments(_ensureIds(d.questionedDocuments || [], "qd"));
          setStep(Math.min(STEPS.length - 1, d.step || 0));
          setResumedAt(d.savedAt);
        }
      }
    } catch {}
  }, [isEditMode, editCaseId]);

  // ------- Auto-save on any change -------
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        role, ident, comps, timeline, strategy, clinical, contact,
        requester, assistedParty, caseShortDescription, questionedDocuments, step,
        savedAt: new Date().toISOString(),
      }));
    } catch {}
  }, [role, ident, comps, timeline, strategy, clinical, contact, requester, assistedParty, caseShortDescription, questionedDocuments, step]);

  const clearDraft = () => {
    localStorage.removeItem(STORAGE_KEY);
    setRole("privato"); setIdent(EMPTY.ident); setComps(EMPTY.comps);
    setTimeline([]); setStrategy(EMPTY.strategy); setClinical(EMPTY.clinical); setContact(EMPTY.contact);
    setRequester(EMPTY.requester); setAssistedParty(EMPTY.assistedParty); setCaseShortDescription("");
    setQuestionedDocuments([]);
    setStep(0); setResumedAt(null);
    toast.success("Compilazione salvata cancellata");
  };

  const addComp = () => setComps({ ...comps, comparatives: [...comps.comparatives, {
    id: _mkId("cmp"),
    description: "", year: null, is_original: false, is_official: false,
    contains_signature: true, contains_text: false, contains_numbers: false,
    certainly_attributable: true, contains_full_signature: false,
    // FASE E.1 HOTFIX P1 · TURNO 2 (BUG 2) — partial/estimated date support.
    date: "", date_precision: "unknown",
    date_text: "", date_year: "", date_month: "",
  }]});
  const rmComp = (i) => setComps({ ...comps, comparatives: comps.comparatives.filter((_, k) => k !== i) });
  const updComp = (i, patch) => setComps({ ...comps, comparatives: comps.comparatives.map((c, k) => k === i ? { ...c, ...patch } : c) });

  const addEvent = () => setTimeline([...timeline, {
    id: _mkId("ev"),
    event_macro: "", event_subtype: "", event_subtype_other: "",
    title: "", description: "", location: "", source: "",
    // FASE E.1 HOTFIX P1 · TURNO 2 (BUG 2/3) — default precision "unknown"
    // (no more hardcoded "exact" with empty date).
    date: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "",
    notes: "",
    // Legacy field kept null for retrocompat readers
    category: "", event_kind: "", event_kind_other: "", label: "",
  }]);
  const rmEvent = (i) => setTimeline(timeline.filter((_, k) => k !== i));
  const updEvent = (i, patch) => setTimeline(timeline.map((e, k) => k === i ? { ...e, ...patch } : e));

  // ------- Progress calculation -------
  const progress = useMemo(() => {
    let done = 0, total = 7;
    if (role) done++;
    if (requester.first_name && requester.last_name && requester.email) done++;
    if (ident.document_type) done++;
    if (ident.document_availability) done++;
    if (comps.comparatives.length > 0) done++;
    if (timeline.length > 0) done++;
    if (strategy.signature_status || strategy.deadline_notes) done++;
    const pct = Math.round((done / total) * 100);
    const remainingMin = STEP_MINUTES.slice(step).reduce((a, b) => a + b, 0);
    return { pct, done, total, remainingMin };
  }, [role, requester, ident, comps, timeline, strategy, step]);

  const submit = async () => {
    if (!userType) { toast.error("Seleziona il tipo di utenza (Privato / Avvocato / Professionista)."); setStep(0); return; }
    if (!privacyConsent) {
      toast.error("È richiesto il consenso al trattamento dei dati personali");
      return;
    }
    setSaving(true);
    try {
      // Build backward-compatible contact from requester
      const contact_payload = {
        contact_name: [requester.first_name, requester.last_name].filter(Boolean).join(" ").trim() || contact.contact_name,
        contact_email: requester.email || contact.contact_email,
        contact_phone: requester.phone || contact.contact_phone,
      };
      // FASE E.3 · TURNO 1 (A) — edit-mode routes to the public revisions
      // endpoint (creates a NEW immutable revision without touching the
      // previous one or its PDF).
      if (isEditMode) {
        const casePayload = {
          role, user_type: userType, identification: ident, comparatives: comps, timeline,
          legal_strategy: strategy, clinical,
          requester,
          assisted_party: assistedParty,
          case_short_description: caseShortDescription,
          questioned_documents: [
            {
              order: 0,
              title: ident.title || (ident.subject?.name ? `Documento in verifica di ${ident.subject.name}` : "Documento in verifica #1"),
              role: ident.role || "principale",
              document_type: ident.document_type,
              date_value: ident.contested_document_date || "",
              date_precision: ident.contested_document_date ? "exact" : "unknown",
              date_text: ident.contested_document_date_text || "",
              date_year: ident.contested_document_date_year || "",
              date_month: ident.contested_document_date_month || "",
              material_support: ident.material_support || ident.document_availability,
              original_status: ident.original_status || "",
              custodian: ident.original_holder,
              disputed_signature_count: ident.disputed_signature_count,
              contains_handwritten_text: ident.contains_handwritten_text,
              contains_handwritten_numbers: ident.contains_handwritten_numbers,
              contains_initials: ident.contains_initials,
              contains_full_signature: ident.contains_full_signature,
              contains_multiple_signatures: ident.contains_multiple_signatures,
              contains_multiple_writers: ident.contains_multiple_writers,
              contested_area_description: ident.contested_area_description || "",
              notes: ident.notes || "",
            },
            ...questionedDocuments.map((qd, i) => ({ ...qd, order: i + 1 })),
          ],
          scoring_version: "multidocument_v2",
          ...contact_payload,
        };
        await createPublicRevision(editCaseId, { case: casePayload });
        toast.success("Nuova revisione salvata — genero il report aggiornato");
        nav(`/case/${editCaseId}`);
        setSaving(false);
        return;
      }
      const c = await createCase({
        // FASE E.3 · TURNO 2C.8 — user_type MUST be included in the create
        // payload; the omission caused every Wizard-created Case to be
        // persisted with the backend default `user_type="private"` regardless
        // of the operator choice (edit-mode branch at line 570 already
        // included it correctly).
        user_type: userType,
        role, identification: ident, comparatives: comps, timeline,
        legal_strategy: strategy, clinical,
        requester,
        assisted_party: assistedParty,
        case_short_description: caseShortDescription,
        // Phase A multi-document: send additional docs. Doc #1 remains identification (source of truth for scoring).
        questioned_documents: [
          {
            order: 0,
            title: ident.title || (ident.subject?.name ? `Documento in verifica di ${ident.subject.name}` : "Documento in verifica #1"),
            role: ident.role || "principale",
            document_type: ident.document_type,
            date_value: ident.contested_document_date || "",
            // FASE E.1 HOTFIX (BUG 3): NEVER hardcode "exact" when the value is empty.
            // If the user declared a date → exact; otherwise → unknown (Consistency C-TEMP-003
            // would otherwise block the PDF, which is the intended safety net).
            date_precision: ident.contested_document_date ? "exact" : "unknown",
            date_text: ident.contested_document_date_text || "",
            date_year: ident.contested_document_date_year || "",
            date_month: ident.contested_document_date_month || "",
            material_support: ident.material_support || ident.document_availability,
            original_status: ident.original_status || "",
            custodian: ident.original_holder,
            disputed_signature_count: ident.disputed_signature_count,
            contains_handwritten_text: ident.contains_handwritten_text,
            contains_handwritten_numbers: ident.contains_handwritten_numbers,
            contains_initials: ident.contains_initials,
            contains_full_signature: ident.contains_full_signature,
            contains_multiple_signatures: ident.contains_multiple_signatures,
            contains_multiple_writers: ident.contains_multiple_writers,
            contested_area_description: ident.contested_area_description || "",
            notes: ident.notes || "",
          },
          ...questionedDocuments.map((qd, i) => ({ ...qd, order: i + 1 })),
        ],
        scoring_version: "multidocument_v2",
        ...contact_payload,
      });
      localStorage.removeItem(STORAGE_KEY);
      toast.success("Fascicolo creato");
      nav(`/case/${c.id}`);
    } catch (e) {
      toast.error("Errore nella creazione del fascicolo");
      console.error(e);
    } finally { setSaving(false); }
  };

  const next = () => {
    if (step === 1) {
      // Validation for new "Richiedente" step
      if (!requester.first_name?.trim() || !requester.last_name?.trim()) {
        toast.error("Nome e cognome del richiedente sono obbligatori");
        return;
      }
      if (!requester.email?.trim()) {
        toast.error("L'e-mail del richiedente è obbligatoria");
        return;
      }
      if ((caseShortDescription || "").length > 1000) {
        toast.error("La descrizione del caso non può superare 1.000 caratteri");
        return;
      }
    }
    // Adjust clinical-step index because we now have 6 steps and clinical is inside step 5 (Strategia)
    // Old check was for step===4 (clinical). Keep symmetry by checking clinical presence on step 5.
    if (step === 5 && !canLeaveStep4) {
      toast.error("Sono stati inseriti dati sanitari: è richiesto il consenso ex art. 9 GDPR");
      return;
    }
    setStep(Math.min(STEPS.length - 1, step + 1));
  };
  const prev = () => setStep(Math.max(0, step - 1));

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl mx-auto px-6 py-10 w-full">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="overline mb-1">Percorso guidato</div>
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Costruzione del fascicolo</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-mono" data-testid="autosave-hint">
              <Save size={11} /> Salvataggio automatico
            </span>
            {resumedAt && (
              <button onClick={clearDraft} data-testid="wizard-clear-draft"
                className="inline-flex items-center gap-1 border hairline hover:border-forensic-red hover:text-forensic-red text-[11px] px-2 py-1 rounded-sm text-slate-500">
                <Trash size={11} /> Cancella compilazione salvata
              </button>
            )}
          </div>
        </div>

        {resumedAt && (
          <div className="mt-4 border hairline bg-forensic-amber/10 border-forensic-amber/30 px-4 py-3 rounded-sm flex items-center gap-3" data-testid="resumed-banner">
            <Info size={16} className="text-forensic-amber shrink-0" />
            <p className="text-sm text-slate-700">
              Compilazione precedente ripresa (salvata il <b>{new Date(resumedAt).toLocaleString("it-IT")}</b>).
              Puoi continuare da dove eri rimasto o cancellare tutto.
            </p>
          </div>
        )}

        {/* Progress bar + meta */}
        <div className="mt-6" data-testid="wizard-progress">
          <div className="flex items-center justify-between text-xs text-slate-600 mb-2">
            <span className="font-mono">STEP {step + 1}/{STEPS.length} · {STEPS[step]}</span>
            <span className="font-mono">
              {progress.pct}% completato · ~{progress.remainingMin} min al termine
            </span>
          </div>
          <div className="h-1.5 bg-slate-200 relative">
            <div className="absolute inset-y-0 left-0 bg-navy transition-all" style={{ width: `${progress.pct}%` }} />
          </div>
        </div>

        {/* Step indicator */}
        <div className="grid grid-cols-7 mt-4 border hairline bg-white">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)}
              className={`p-3 text-center border-r hairline last:border-r-0 transition-colors ${i === step ? "bg-navy text-white" : i < step ? "text-navy hover:bg-navy/5" : "text-slate-500 hover:bg-slate-50"}`}
              data-testid={`step-indicator-${i}`}>
              <div className="font-mono text-[10px]">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-[11px] font-medium mt-1">{s}</div>
            </button>
          ))}
        </div>

        <div className="border hairline bg-white mt-6 p-6 lg:p-10">
          {/* FASE E.3 · TURNO 2A — user_type radio at the top of Wizard step 0. */}
        <div className="mb-6 border border-slate-200 bg-slate-50 p-4 rounded-sm" data-testid="user-type-selector">
          <div className="overline mb-2 text-navy">Come utilizzerai Perizia Ready?</div>
          <div className="grid md:grid-cols-3 gap-3">
            {[
              { v: "private", l: "Privato", d: "Sto valutando un caso personale e desidero capire come procedere." },
              { v: "lawyer", l: "Avvocato", d: "Utilizzo Perizia Ready nell'ambito della mia attività professionale." },
              { v: "professional", l: "Professionista", d: "Grafologo, perito, consulente tecnico o altro professionista." },
            ].map((opt) => (
              <label key={opt.v}
                     data-testid={`user-type-${opt.v}`}
                     className={`cursor-pointer border-2 p-3 rounded-sm transition-colors ${userType === opt.v ? "border-navy bg-navy/5" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <input type="radio" name="user_type" value={opt.v}
                       checked={userType === opt.v}
                       onChange={() => setUserType(opt.v)}
                       className="sr-only" />
                <div className="font-semibold text-ink">{opt.l}</div>
                <p className="text-xs text-slate-600 mt-1 leading-snug">{opt.d}</p>
              </label>
            ))}
          </div>
        </div>
        {step === 0 && (
            <div data-testid="step-user-type-only">
              <p className="text-sm text-slate-600 mb-6">
                La scelta modula la profondità delle domande, la terminologia e il tono del report finale.
              </p>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-requester">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Richiedente e caso</h2>
                <p className="text-sm text-slate-600 mt-2">
                  Compila i dati anagrafici del richiedente e, se applicabile, della parte assistita.
                </p>
                <div className="mt-3 border-l-2 border-navy bg-navy/5 px-4 py-3 text-xs text-slate-700 leading-snug" data-testid="requester-disclaimer">
                  <b>I dati inseriti in questa scheda non servono per la registrazione</b> ad alcun servizio,
                  ma esclusivamente per la generazione del report e per la sua eventuale gestione in caso
                  di invio al consulente. Non vengono utilizzati ai fini dello scoring del fascicolo.
                </div>
              </div>

              {/* ------ Blocco 1: Dati del richiedente ------ */}
              <div className="border hairline p-4 bg-paper" data-testid="requester-block">
                <div className="overline mb-3">Dati del richiedente</div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Nome *">
                    <input type="text" value={requester.first_name}
                      onChange={(e) => setRequester({ ...requester, first_name: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="requester-first-name" required />
                  </Field>
                  <Field label="Cognome *">
                    <input type="text" value={requester.last_name}
                      onChange={(e) => setRequester({ ...requester, last_name: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="requester-last-name" required />
                  </Field>
                  {role === "avvocato" && (
                    <>
                      <Field label="Titolo professionale">
                        <Select value={requester.title || "Avv."}
                          onChange={(v) => setRequester({ ...requester, title: v })}
                          options={LEGAL_TITLES} testid="requester-title" hideEmpty />
                      </Field>
                      <Field label="Studio legale o organizzazione (facoltativo)">
                        <input type="text" value={requester.organization}
                          onChange={(e) => setRequester({ ...requester, organization: e.target.value })}
                          placeholder="Es. Studio Legale Bianchi & Associati"
                          className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                          data-testid="requester-organization" />
                      </Field>
                    </>
                  )}
                  <Field label="E-mail *">
                    <input type="email" value={requester.email}
                      onChange={(e) => setRequester({ ...requester, email: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="requester-email" required />
                  </Field>
                  <Field label="Telefono (facoltativo)">
                    <input type="tel" value={requester.phone}
                      onChange={(e) => setRequester({ ...requester, phone: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="requester-phone" />
                  </Field>
                </div>
              </div>

              {/* ------ Blocco 2: Parte assistita (solo avvocato/professionista) ------ */}
              {(role === "avvocato" || role === "professionista") && (
                <div className="border hairline p-4 bg-paper" data-testid="assisted-party-block">
                  <div className="overline mb-3">Parte assistita nel procedimento</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Nome della parte assistita">
                      <input type="text" value={assistedParty.first_name}
                        onChange={(e) => setAssistedParty({ ...assistedParty, first_name: e.target.value })}
                        className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                        data-testid="assisted-first-name" />
                    </Field>
                    <Field label="Cognome della parte assistita">
                      <input type="text" value={assistedParty.last_name}
                        onChange={(e) => setAssistedParty({ ...assistedParty, last_name: e.target.value })}
                        className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                        data-testid="assisted-last-name" />
                    </Field>
                    <Field label="Posizione processuale">
                      <Select value={assistedParty.role}
                        onChange={(v) => setAssistedParty({ ...assistedParty, role: v, ...(v !== "altro" ? { role_other: "" } : {}) })}
                        options={ASSISTED_PARTY_ROLES} testid="assisted-role" />
                    </Field>
                    {assistedParty.role === "altro" && (
                      <Field label="Specificare la posizione processuale">
                        <input type="text" value={assistedParty.role_other}
                          onChange={(e) => setAssistedParty({ ...assistedParty, role_other: e.target.value })}
                          className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                          data-testid="assisted-role-other" />
                      </Field>
                    )}
                  </div>
                </div>
              )}

              {/* ------ Blocco 2b: Ruolo nel caso (solo privato) ------ */}
              {role === "privato" && (
                <div className="border hairline p-4 bg-paper" data-testid="private-role-block">
                  <div className="overline mb-3">Ruolo nel caso</div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Field label="Il tuo ruolo nel caso">
                      <Select value={assistedParty.role}
                        onChange={(v) => setAssistedParty({ ...assistedParty, role: v, ...(v !== "altro" ? { role_other: "" } : {}) })}
                        options={PRIVATE_CASE_ROLES} testid="private-role" />
                    </Field>
                    {assistedParty.role === "altro" && (
                      <Field label="Specificare il ruolo">
                        <input type="text" value={assistedParty.role_other}
                          onChange={(e) => setAssistedParty({ ...assistedParty, role_other: e.target.value })}
                          className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                          data-testid="private-role-other" />
                      </Field>
                    )}
                  </div>
                </div>
              )}

              {/* ------ Blocco 3: Breve descrizione del caso ------ */}
              <div className="border hairline p-4 bg-paper" data-testid="case-description-block">
                <div className="overline mb-2">Breve descrizione del caso (facoltativo)</div>
                <p className="text-xs text-slate-600 leading-snug mb-3">
                  Inserisci una breve sintesi utile a comprendere il contesto del fascicolo.
                  Non è necessario riportare tutti i dettagli: le informazioni tecniche e documentali
                  verranno raccolte nelle sezioni successive.
                </p>
                <textarea
                  value={caseShortDescription}
                  onChange={(e) => setCaseShortDescription(e.target.value.slice(0, 1000))}
                  placeholder="Descrivi sinteticamente il contesto del caso, il documento contestato, la posizione delle parti e l'obiettivo della richiesta."
                  rows={5}
                  className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
                  data-testid="case-short-description"
                  maxLength={1000}
                />
                <div className="mt-1 text-[11px] text-slate-500 font-mono text-right">
                  {caseShortDescription.length} / 1.000 caratteri
                </div>
              </div>
            </div>
          )}


          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Il documento in contestazione</h2>
                <p className="text-sm text-slate-600 mt-2">Fornisci le informazioni essenziali sul documento oggetto della verifica. Se sono contestati più documenti, aggiungili di seguito.</p>
              </div>

              {/* ============ Documento in verifica #1 — allineato allo schema Doc #2+ ============ */}
              <div className="border hairline p-3 bg-paper space-y-2" data-testid="qdoc-primary">
                <div className="flex items-center justify-between gap-2">
                  <div className="overline">Documento in verifica #1 (principale)</div>
                </div>
                <div className="grid md:grid-cols-[2fr_1fr] gap-2">
                  <input type="text" value={ident.title || ""} onChange={(e) => setIdent({ ...ident, title: e.target.value })}
                    placeholder="Denominazione sintetica (es. Testamento olografo del 10/10/2024)"
                    className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                    data-testid="ident-title" />
                  <Select value={ident.role || "principale"} onChange={(v) => setIdent({ ...ident, role: v })}
                    options={QDOC_ROLES} testid="ident-role" hideEmpty />
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <Select value={ident.document_type} onChange={(v) => setIdent({ ...ident, document_type: v })}
                    options={DOC_TYPES} testid="ident-document-type" />
                  <input type="date" min="1800-01-01" max="2200-12-31" value={ident.contested_document_date || ""}
                    onChange={(e) => setIdent({ ...ident, contested_document_date: e.target.value })}
                    className="border hairline px-2 py-2 rounded-sm text-sm font-mono bg-white"
                    data-testid="ident-doc-date" />
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <Select value={ident.material_support || ""}
                    onChange={(v) => setIdent({ ...ident, material_support: v, document_availability: v })}
                    options={QDOC_MATERIAL} testid="ident-material" />
                  <Select value={ident.original_status || ""} onChange={(v) => setIdent({ ...ident, original_status: v })}
                    options={QDOC_ORIGINAL_STATUS} testid="ident-original-status" />
                </div>
                <div className="grid md:grid-cols-[1fr_auto] gap-2">
                  <input type="text" value={ident.original_holder || ""}
                    onChange={(e) => setIdent({ ...ident, original_holder: e.target.value })}
                    placeholder="Custode (es. cliente, cancelleria, notaio)"
                    className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                    data-testid="ident-holder" />
                  <div className="flex items-center gap-1">
                    <label className="text-xs text-slate-600">N° firme contestate</label>
                    <input type="number" min="0" value={ident.disputed_signature_count ?? ""}
                      onChange={(e) => setIdent({ ...ident, disputed_signature_count: e.target.value === "" ? null : parseInt(e.target.value) })}
                      className="w-16 border hairline px-2 py-2 rounded-sm text-sm font-mono bg-white text-center"
                      data-testid="ident-sig-count" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                  <Chk label="Contiene testo manoscritto" checked={ident.contains_handwritten_text} onChange={(v) => setIdent({ ...ident, contains_handwritten_text: v })} testid="ident-contains-text" />
                  <Chk label="Contiene numeri manoscritti" checked={ident.contains_handwritten_numbers} onChange={(v) => setIdent({ ...ident, contains_handwritten_numbers: v })} testid="ident-contains-numbers" />
                  <Chk label="Contiene sigle / paraffe" checked={ident.contains_initials} onChange={(v) => setIdent({ ...ident, contains_initials: v })} testid="ident-contains-initials" />
                  <Chk label="Contiene firma per esteso" checked={ident.contains_full_signature} onChange={(v) => setIdent({ ...ident, contains_full_signature: v })} testid="ident-contains-full-signature" />
                  <Chk label="Contiene più firme" checked={ident.contains_multiple_signatures} onChange={(v) => setIdent({ ...ident, contains_multiple_signatures: v })} testid="ident-contains-multiple-signatures" />
                  <Chk label="Presenza di più scriventi" checked={ident.contains_multiple_writers} onChange={(v) => setIdent({ ...ident, contains_multiple_writers: v })} testid="ident-contains-multiple-writers" />
                </div>
                <input type="text" value={ident.contested_area_description || ""}
                  onChange={(e) => setIdent({ ...ident, contested_area_description: e.target.value })}
                  placeholder="Descrizione dell'area contestata (facoltativo)"
                  className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                  data-testid="ident-contested-area" />
                <input type="text" value={ident.notes || ""}
                  onChange={(e) => setIdent({ ...ident, notes: e.target.value })}
                  placeholder="Note (facoltativo)"
                  className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                  data-testid="ident-notes" />
              </div>

              {/* Contesto procedurale del documento in contestazione */}
              <div className="border-t hairline pt-4 mt-2">
                <div className="overline mb-3">Contesto procedurale</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <BoolField label="Prodotto in giudizio?" value={ident.already_in_court} onChange={(v) => setIdent({ ...ident, already_in_court: v })} testid="ident-court" />
                  <BoolField label="Disconoscimento avvenuto?" value={ident.disconoscimento} onChange={(v) => setIdent({ ...ident, disconoscimento: v })} testid="ident-disconoscimento" />
                  <Field label="Fase">
                    <Select value={ident.phase} onChange={(v) => setIdent({ ...ident, phase: v })} options={[{ v: "stragiudiziale", l: "Stragiudiziale" }, { v: "mediazione_obbligatoria", l: "Mediazione obbligatoria" }, { v: "giudiziale", l: "Giudiziale" }, { v: "appello", l: "Appello" }]} testid="ident-phase" />
                  </Field>
                </div>
              </div>

              {/* ============ FASE A — Documenti in verifica aggiuntivi ============ */}
              <div className="border-t hairline pt-5 mt-4" data-testid="qdoc-multi-block">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline">Ulteriori documenti in verifica (facoltativo)</div>
                  <button type="button" onClick={() => setQuestionedDocuments([...questionedDocuments, {
                    id: _mkId("qd"),
                    title: "", role: "secondario",
                    document_type: "",
                    // FASE E.1 HOTFIX (BUG 3): DO NOT hardcode "exact" on empty date.
                    // Precision is derived from the value the user actually provides.
                    date_value: "", date_precision: "unknown", date_display: "",
                    date_text: "", date_year: "", date_month: "",
                    material_support: "", original_status: "",
                    custodian: "", disputed_signature_count: null,
                    contains_handwritten_text: null, contains_handwritten_numbers: null,
                    contains_initials: null, contains_full_signature: null,
                    contains_multiple_signatures: null, contains_multiple_writers: null,
                    contested_area_description: "", notes: "",
                  }])} className="text-xs text-navy hover:underline flex items-center gap-1" data-testid="add-qdoc-btn">
                    <Plus size={11} /> Aggiungi documento in verifica
                  </button>
                </div>
                <div className="border-l-2 border-forensic-amber bg-forensic-amber/10 px-3 py-2 text-[11px] text-slate-700 leading-snug mb-3" data-testid="qdoc-multidoc-notice">
                  <b>Nota amministrativa.</b> Puoi inserire più documenti in verifica con la stessa struttura del documento principale: il modello dati li conserva
                  e verranno mostrati nel CRM e nel riepilogo. La valutazione automatica per-documento (indici, timeline
                  specifica, alert, assumibilità) è applicata a ciascun documento. In questa versione gli indici
                  sintetici globali continuano a riferirsi al documento principale (#1).
                </div>
                {questionedDocuments.length === 0 ? (
                  <div className="text-xs text-slate-500 italic">Nessun documento aggiuntivo — è sufficiente il documento compilato sopra.</div>
                ) : (
                  <div className="space-y-3">
                    {questionedDocuments.map((qd, i) => {
                      const upd = (patch) => setQuestionedDocuments(questionedDocuments.map((x, k) => k === i ? { ...x, ...patch } : x));
                      const rm = () => setQuestionedDocuments(questionedDocuments.filter((_, k) => k !== i));
                      const dup = () => setQuestionedDocuments([
                        ...questionedDocuments.slice(0, i + 1),
                        { ...qd, id: _mkId("qd"), title: (qd.title || "") + " (copia)" },
                        ...questionedDocuments.slice(i + 1),
                      ]);
                      const move = (delta) => {
                        const j = i + delta;
                        if (j < 0 || j >= questionedDocuments.length) return;
                        const arr = [...questionedDocuments];
                        [arr[i], arr[j]] = [arr[j], arr[i]];
                        setQuestionedDocuments(arr);
                      };
                      return (
                        <div key={qd.id} className="border hairline p-3 bg-paper space-y-2" data-testid={`qdoc-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="overline">Documento in verifica #{i + 2}</div>
                            <div className="flex items-center gap-1">
                              <button type="button" onClick={() => move(-1)} disabled={i === 0} className="text-slate-500 hover:text-navy disabled:opacity-30 text-xs px-1" data-testid={`qdoc-${i}-up`} title="Sposta su">↑</button>
                              <button type="button" onClick={() => move(1)} disabled={i === questionedDocuments.length - 1} className="text-slate-500 hover:text-navy disabled:opacity-30 text-xs px-1" data-testid={`qdoc-${i}-down`} title="Sposta giù">↓</button>
                              <button type="button" onClick={dup} className="text-slate-500 hover:text-navy text-xs px-1" data-testid={`qdoc-${i}-dup`} title="Duplica">⧉</button>
                              <button type="button" onClick={rm} className="text-slate-500 hover:text-forensic-red p-1" data-testid={`qdoc-${i}-rm`} title="Elimina"><Trash2 size={13} /></button>
                            </div>
                          </div>
                          <div className="grid md:grid-cols-[2fr_1fr] gap-2">
                            <input type="text" value={qd.title || ""} onChange={(e) => upd({ title: e.target.value })}
                              placeholder="Denominazione sintetica (es. Procura speciale del 10/10/2024)"
                              className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`qdoc-${i}-title`} />
                            <Select value={qd.role || "secondario"} onChange={(v) => upd({ role: v })}
                              options={QDOC_ROLES} testid={`qdoc-${i}-role`} hideEmpty />
                          </div>
                          <div className="grid md:grid-cols-2 gap-2">
                            <input type="text" value={qd.document_type || ""} onChange={(e) => upd({ document_type: e.target.value })}
                              placeholder="Tipo di documento (es. testamento olografo)"
                              className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`qdoc-${i}-type`} />
                            <div className="grid grid-cols-[1fr_1fr] gap-2">
                              <Select value={qd.date_precision || "exact"}
                                onChange={(v) => upd({ date_precision: v, ...(v === "exact" ? { date_text: "", date_year: "", date_month: "" } : { date_value: "" }) })}
                                options={DATE_PRECISION_OPTIONS} testid={`qdoc-${i}-date-precision`} hideEmpty />
                              {(qd.date_precision || "exact") === "exact" ? (
                                <input type="date" min="1800-01-01" max="2200-12-31"
                                  value={qd.date_value || ""} onChange={(e) => upd({ date_value: e.target.value })}
                                  className="border hairline px-2 py-2 rounded-sm text-sm font-mono bg-white"
                                  data-testid={`qdoc-${i}-date`} />
                              ) : qd.date_precision === "unknown" ? (
                                <input type="text" value="Data non nota" disabled
                                  className="border hairline px-3 py-2 rounded-sm text-sm bg-slate-100 text-slate-500"
                                  data-testid={`qdoc-${i}-date-text`} />
                              ) : (
                                <input type="text" value={qd.date_text || ""}
                                  onChange={(e) => upd({ date_text: e.target.value })}
                                  placeholder={qd.date_precision === "month_year" ? "es. Aprile 2025" : qd.date_precision === "year_only" ? "es. 2025" : "es. circa 2025"}
                                  className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                                  data-testid={`qdoc-${i}-date-text`} />
                              )}
                            </div>
                          </div>
                          {(qd.date_precision === "month_year" || qd.date_precision === "year_only" || qd.date_precision === "estimated") && (
                            <div className="flex gap-1">
                              <input type="number" min="1800" max="2200" value={qd.date_year || ""}
                                onChange={(e) => upd({ date_year: e.target.value })}
                                placeholder="Anno"
                                className="w-24 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                                data-testid={`qdoc-${i}-date-year`} />
                              {qd.date_precision === "month_year" && (
                                <input type="number" min="1" max="12" value={qd.date_month || ""}
                                  onChange={(e) => upd({ date_month: e.target.value })}
                                  placeholder="Mese"
                                  className="w-20 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                                  data-testid={`qdoc-${i}-date-month`} />
                              )}
                            </div>
                          )}
                          <div className="grid md:grid-cols-2 gap-2">
                            <Select value={qd.material_support || ""} onChange={(v) => upd({ material_support: v })}
                              options={QDOC_MATERIAL} testid={`qdoc-${i}-material`} />
                            <Select value={qd.original_status || ""} onChange={(v) => upd({ original_status: v })}
                              options={QDOC_ORIGINAL_STATUS} testid={`qdoc-${i}-original-status`} />
                          </div>
                          <div className="grid md:grid-cols-[1fr_auto] gap-2">
                            <input type="text" value={qd.custodian || ""} onChange={(e) => upd({ custodian: e.target.value })}
                              placeholder="Custode (es. cliente, cancelleria, notaio)"
                              className="border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`qdoc-${i}-custodian`} />
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-slate-600">N° firme contestate</label>
                              <input type="number" min="0" value={qd.disputed_signature_count ?? ""}
                                onChange={(e) => upd({ disputed_signature_count: e.target.value === "" ? null : parseInt(e.target.value) })}
                                className="w-16 border hairline px-2 py-2 rounded-sm text-sm font-mono bg-white text-center"
                                data-testid={`qdoc-${i}-sig-count`} />
                            </div>
                          </div>
                          <div className="border-t hairline pt-2 mt-1" data-testid={`qdoc-${i}-structural`}>
                            <div className="overline mb-1">Struttura del documento</div>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-1">
                              <Chk label="Testo manoscritto" checked={qd.contains_handwritten_text} onChange={(v) => upd({ contains_handwritten_text: v })} testid={`qdoc-${i}-contains-text`} />
                              <Chk label="Numeri manoscritti" checked={qd.contains_handwritten_numbers} onChange={(v) => upd({ contains_handwritten_numbers: v })} testid={`qdoc-${i}-contains-numbers`} />
                              <Chk label="Sigle / paraffe" checked={qd.contains_initials} onChange={(v) => upd({ contains_initials: v })} testid={`qdoc-${i}-contains-initials`} />
                              <Chk label="Firma per esteso" checked={qd.contains_full_signature} onChange={(v) => upd({ contains_full_signature: v })} testid={`qdoc-${i}-contains-full-sig`} />
                              <Chk label="Più firme" checked={qd.contains_multiple_signatures} onChange={(v) => upd({ contains_multiple_signatures: v })} testid={`qdoc-${i}-contains-multi-sig`} />
                              <Chk label="Più scriventi" checked={qd.contains_multiple_writers} onChange={(v) => upd({ contains_multiple_writers: v })} testid={`qdoc-${i}-contains-multi-writers`} />
                            </div>
                            <input type="text" value={qd.contested_area_description || ""}
                              onChange={(e) => upd({ contested_area_description: e.target.value })}
                              placeholder="Zona contestata (facoltativo)"
                              className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white mt-2"
                              data-testid={`qdoc-${i}-contested-area`} />
                          </div>
                          <input type="text" value={qd.notes || ""} onChange={(e) => upd({ notes: e.target.value })}
                            placeholder="Note (facoltativo)"
                            className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                            data-testid={`qdoc-${i}-notes`} />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>


              <div className="border-t hairline pt-4 mt-2">
                <label className="inline-flex items-start gap-2 cursor-pointer" data-testid="ident-mediazione-obbligatoria-label">
                  <input type="checkbox" checked={!!ident.mediazione_obbligatoria}
                    onChange={(e) => setIdent({ ...ident, mediazione_obbligatoria: e.target.checked })}
                    className="mt-1 accent-navy"
                    data-testid="ident-mediazione-obbligatoria" />
                  <span className="text-sm text-slate-700 leading-snug">
                    <b>Mediazione obbligatoria</b> — spuntare se la materia rientra nell'art. 5, d.lgs. 28/2010
                    (locazione, divisione, successioni, condominio, ecc.) e il procedimento di mediazione è
                    condizione di procedibilità.
                  </span>
                </label>
              </div>

              <div className="border-t hairline pt-5 mt-2">
                <div className="overline mb-3">Soggetto scrivente <span className="text-slate-400 lowercase text-[9px]">(facoltativo)</span></div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Nome del soggetto scrivente">
                    <input type="text" value={ident.subject?.name || ""}
                      onChange={(e) => setIdent({ ...ident, subject: { ...(ident.subject || {}), name: e.target.value } })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="subject-name" />
                  </Field>
                  <Field label="Data di nascita del soggetto">
                    <input type="date" min="1800-01-01" max="2200-12-31" value={ident.subject?.dob || ""}
                      onChange={(e) => setIdent({ ...ident, subject: { ...(ident.subject || {}), dob: e.target.value } })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono"
                      data-testid="subject-dob" />
                  </Field>
                  <Field label="Data del decesso (se applicabile)">
                    <input type="date" min="1800-01-01" max="2200-12-31" value={ident.subject?.deceased_date || ""}
                      onChange={(e) => setIdent({ ...ident, subject: { ...(ident.subject || {}), deceased_date: e.target.value } })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono"
                      data-testid="subject-deceased" />
                  </Field>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Scritture di comparazione</h2>
                <p className="text-sm text-slate-600 mt-2">Inserisci i documenti comparativi disponibili. Aggiungerne di certamente attribuibili aumenta la lavorabilità del fascicolo.</p>
              </div>
              <div className="space-y-3">
                {comps.comparatives.map((c, i) => (
                  <div key={c.id} className="border hairline p-4 bg-paper" data-testid={`comp-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="overline">Comparativa #{String(i + 1).padStart(2, "0")}</div>
                      <button onClick={() => rmComp(i)} className="text-slate-500 hover:text-forensic-red" data-testid={`comp-remove-${i}`}><Trash2 size={16} /></button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3 mt-2">
                      <input type="text" value={c.description} onChange={(e) => updComp(i, { description: e.target.value })}
                        placeholder="Es. Rogito notarile, contratto banca..." className="border hairline px-3 py-2 rounded-sm bg-white text-sm col-span-2" />
                      <div className="col-span-2 grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-2" data-testid={`comp-${i}-date-block`}>
                        <Select value={c.date_precision || (c.date ? "exact" : (c.year ? "year_only" : "unknown"))}
                          onChange={(v) => updComp(i, { date_precision: v, ...(v === "exact" ? { date_text: "", date_year: "", date_month: "" } : { date: "" }) })}
                          options={DATE_PRECISION_OPTIONS} testid={`comp-${i}-date-precision`} hideEmpty />
                        {(c.date_precision || (c.date ? "exact" : "unknown")) === "exact" ? (
                          <input type="date" min="1800-01-01" max="2200-12-31" value={c.date || ""}
                            onChange={(e) => updComp(i, { date: e.target.value })}
                            className="border hairline px-3 py-2 rounded-sm bg-white text-sm font-mono"
                            data-testid={`comp-${i}-date`} placeholder="Data documento" />
                        ) : c.date_precision === "unknown" ? (
                          <input type="text" value="Data non nota" disabled
                            className="border hairline px-3 py-2 rounded-sm bg-slate-100 text-sm text-slate-500"
                            data-testid={`comp-${i}-date-text`} />
                        ) : (
                          <input type="text" value={c.date_text || ""}
                            onChange={(e) => updComp(i, { date_text: e.target.value })}
                            placeholder={c.date_precision === "month_year" ? "es. Aprile 2025" : c.date_precision === "year_only" ? "es. 2025" : "es. circa 2025"}
                            className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                            data-testid={`comp-${i}-date-text`} />
                        )}
                      </div>
                      {(c.date_precision === "month_year" || c.date_precision === "year_only" || c.date_precision === "estimated") && (
                        <div className="col-span-2 flex gap-1">
                          <input type="number" min="1800" max="2200" value={c.date_year || (c.year ? String(c.year) : "")}
                            onChange={(e) => updComp(i, { date_year: e.target.value, year: e.target.value ? parseInt(e.target.value) : null })}
                            placeholder="Anno"
                            className="w-24 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                            data-testid={`comp-${i}-date-year`} />
                          {c.date_precision === "month_year" && (
                            <input type="number" min="1" max="12" value={c.date_month || ""}
                              onChange={(e) => updComp(i, { date_month: e.target.value })}
                              placeholder="Mese"
                              className="w-20 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                              data-testid={`comp-${i}-date-month`} />
                          )}
                        </div>
                      )}
                      <select value={c.source_material || ""} onChange={(e) => updComp(i, { source_material: e.target.value, is_original: e.target.value === "originale" })}
                        className="border hairline px-3 py-2 rounded-sm bg-white text-sm">
                        <option value="">Supporto materiale…</option>
                        {SOURCE_MATERIALS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                      </select>
                      <input type="text" value={c.custodian || ""} onChange={(e) => updComp(i, { custodian: e.target.value })}
                        placeholder="Custode / provenienza" className="border hairline px-3 py-2 rounded-sm bg-white text-sm col-span-2" />
                      <div className="grid grid-cols-2 gap-2 text-xs col-span-2">
                        <Chk label="Documento ufficiale" checked={c.is_official} onChange={(v) => updComp(i, { is_official: v })} />
                        <Chk label="Firma abbreviata / sigla" checked={c.is_short_signature} onChange={(v) => updComp(i, { is_short_signature: v })} />
                        <Chk label="Contiene testo manoscritto" checked={c.contains_text} onChange={(v) => updComp(i, { contains_text: v })} />
                        <Chk label="Contiene numeri" checked={c.contains_numbers} onChange={(v) => updComp(i, { contains_numbers: v })} />
                        <Chk label="Attribuibile con certezza" checked={c.certainly_attributable} onChange={(v) => updComp(i, { certainly_attributable: v })} />
                        <Chk label="Firma per esteso" checked={c.contains_full_signature} onChange={(v) => updComp(i, { contains_full_signature: v })} />
                        {c._legacy_spontaneous !== undefined && (
                          <div className="col-span-2 text-[10px] text-slate-500 italic border-l-2 border-slate-300 pl-2 mt-1" data-testid={`comp-${i}-legacy-spontaneous`}>
                            Dato legacy (vecchio schema): "Spontanea" = {String(c._legacy_spontaneous)} — non modificabile, conservato per audit.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={addComp} className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-sm text-sm transition-colors" data-testid="add-comp-btn">
                <Plus size={16} /> Aggiungi comparativa
              </button>

              {/* Gamification hint */}
              {comps.comparatives.length > 0 && comps.comparatives.length < 8 && (
                <div className="border-l-2 border-forensic-green bg-forensic-green/5 px-4 py-3 text-xs text-slate-700 leading-snug" data-testid="wizard-gamification-hint">
                  <b>Suggerimento:</b> aggiungendo almeno {Math.max(3, 8 - comps.comparatives.length)} ulteriori comparative attribuibili al soggetto, la rappresentatività documentale potrebbe migliorare. <i>Sulla base delle informazioni dichiarate</i>.
                </div>
              )}
              {comps.comparatives.length > 0 && !comps.comparatives.some((c) => c.is_original) && (
                <div className="border-l-2 border-forensic-amber bg-forensic-amber/5 px-4 py-3 text-xs text-slate-700 leading-snug">
                  <b>Suggerimento:</b> nessuna delle comparative dichiarate risulta in originale. Reperire almeno alcuni documenti originali attribuibili potrebbe migliorare l'indice.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t hairline">
                <Field label="Periodo temporale coperto (anni)">
                  <input type="number" value={comps.period_covered_years ?? ""} onChange={(e) => setComps({ ...comps, period_covered_years: e.target.value ? Number(e.target.value) : null })}
                    className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="comps-period" />
                </Field>
                <Chk label="Sono presenti documenti già riconosciuti, verificati o autenticati" checked={comps.already_verified_documents} onChange={(v) => setComps({ ...comps, already_verified_documents: v })} testid="comps-verified" />
              </div>

              {/* Età anagrafica + Lateralità + Occhiali */}
              <div className="border-t hairline pt-6 mt-2">
                <div className="overline mb-3">Caratteristiche personali dello scrivente <span className="text-slate-400 lowercase text-[9px]">(facoltativo)</span></div>

                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Data di nascita dello scrivente">
                    <input type="date" min="1800-01-01" max="2200-12-31" value={ident.subject_dob || ""}
                      onChange={(e) => setIdent({ ...ident, subject_dob: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono"
                      data-testid="ident-subject-dob" />
                    <p className="text-[10px] text-slate-500 mt-1">
                      L'età alla data del documento in verifica verrà calcolata automaticamente.
                    </p>
                  </Field>
                  <Field label="Oppure età in anni (se la data di nascita non è nota)">
                    <input type="number" min="0" max="120"
                      value={ident.subject_age_years ?? ""}
                      onChange={(e) => setIdent({ ...ident, subject_age_years: e.target.value ? Number(e.target.value) : null })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white font-mono"
                      placeholder="Es. 78" data-testid="ident-subject-age" />
                  </Field>
                </div>
                {(ident.subject_dob && ident.subject_age_years) && (
                  <div className="text-[11px] text-forensic-amber bg-forensic-amber/10 border border-forensic-amber/40 rounded-sm px-3 py-2 mt-2" data-testid="age-priority-hint">
                    Sono valorizzati sia data di nascita sia età: <b>verrà considerata la data di nascita</b>
                    (l'età dichiarata è indicativa e potrebbe non essere coerente).
                  </div>
                )}

                <div className="mt-4">
                  <Field label="Lateralità dello scrivente">
                    <div className="flex flex-wrap gap-2">
                      {LATERALITY_OPTIONS.map((o) => (
                        <button key={o.v} type="button"
                          onClick={() => setIdent({ ...ident, laterality: ident.laterality === o.v ? "" : o.v })}
                          className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${ident.laterality === o.v ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"}`}
                          data-testid={`laterality-${o.v}`}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>

                <div className="mt-4">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={!!ident.uses_glasses}
                      onChange={(e) => setIdent({ ...ident, uses_glasses: e.target.checked })}
                      className="accent-navy" data-testid="ident-uses-glasses" />
                    <span className="text-sm">Uso di occhiali da vista</span>
                  </label>
                  <div className="mt-2 overline mb-1">Problemi visivi dichiarati</div>
                  <div className="flex flex-wrap gap-2">
                    {VISUAL_ISSUES_OPTIONS.map((v) => {
                      const sel = (ident.visual_issues || []).includes(v);
                      return (
                        <button key={v} type="button"
                          onClick={() => setIdent({
                            ...ident,
                            visual_issues: sel
                              ? (ident.visual_issues || []).filter((x) => x !== v)
                              : [...(ident.visual_issues || []), v],
                          })}
                          className={`text-xs px-3 py-1 rounded-sm border transition-colors ${sel ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"}`}
                          data-testid={`visual-issue-${v.replace(/\s/g, '-')}`}>
                          {v}
                        </button>
                      );
                    })}
                  </div>
                  <input type="text" value={ident.visual_notes || ""}
                    onChange={(e) => setIdent({ ...ident, visual_notes: e.target.value })}
                    className="w-full border hairline px-3 py-2 rounded-sm bg-white mt-2 text-sm"
                    placeholder="Note aggiuntive sui problemi visivi (facoltativo)"
                    data-testid="visual-notes" />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Cronologia del caso</h2>
                <p className="text-sm text-slate-600 mt-2">
                  Inserisci gli eventi <b>non sanitari</b> utili a ricostruire la storia del documento e del procedimento.
                  In questa sezione puoi indicare la redazione, sottoscrizione, produzione o contestazione del documento,
                  gli eventi processuali e stragiudiziali e altri fatti personali o contestuali rilevanti,
                  come trasferimenti di residenza o soggiorni.
                </p>
                <p className="text-xs text-slate-500 mt-1 italic">
                  Le scritture comparative e la documentazione sanitaria devono essere inserite nelle rispettive sezioni dedicate
                  e saranno aggiunte automaticamente alla timeline integrata.
                </p>
              </div>
              {timeline.map((e, i) => {
                const isLegacy = !e.event_macro && !!e.category;
                const subtypes = TIMELINE_SUBTYPES[e.event_macro] || [];
                const currentTitle = (e.title || "").trim();
                const currentDesc = (e.description || "").trim();
                const combined = `${currentTitle} ${currentDesc}`.toLowerCase();
                const isPersonalOrAltro = e.event_macro === "personale" || e.event_macro === "altro";
                const looksSanitary = isPersonalOrAltro && SANITARY_KEYWORDS_STRONG.some((k) => combined.includes(k));
                const sanitaryDismissed = !!e._sanitary_dismissed;
                const showSanitaryWarning = looksSanitary && !sanitaryDismissed;

                return (
                  <div key={e.id} className={`border p-3 bg-paper space-y-3 ${isLegacy ? "border-slate-400 bg-slate-50" : "hairline"}`} data-testid={`event-${i}`}>
                    {isLegacy && (
                      <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-100 border border-slate-300 px-2 py-1 rounded-sm">
                        <Info size={12} />
                        <span>Evento legacy (schema precedente). Categoria: <b>{e.category}</b>. Per modificarlo, rimuovilo e ricrealo con la nuova struttura.</span>
                        <button onClick={() => rmEvent(i)} className="ml-auto text-forensic-red hover:underline">Rimuovi</button>
                      </div>
                    )}
                    {!isLegacy && (
                      <>
                        {/* Riga 1: macro + rimuovi */}
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 items-center">
                          <Select
                            value={e.event_macro || ""}
                            onChange={(v) => updEvent(i, { event_macro: v, event_subtype: "", event_subtype_other: "" })}
                            options={[{ v: "", l: "— Tipo di evento —" }, ...TIMELINE_MACROS]}
                            testid={`event-${i}-macro`}
                          />
                          <button onClick={() => rmEvent(i)} className="text-slate-500 hover:text-forensic-red p-2"><Trash2 size={16} /></button>
                        </div>

                        {/* Riga 2: sottotipo dipendente */}
                        {e.event_macro && subtypes.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2">
                            <Select
                              value={e.event_subtype || ""}
                              onChange={(v) => updEvent(i, { event_subtype: v, ...(v !== "altro" ? { event_subtype_other: "" } : {}) })}
                              options={[{ v: "", l: "— Sottotipo —" }, ...subtypes]}
                              testid={`event-${i}-subtype`}
                            />
                            {e.event_subtype === "altro" && (
                              <input type="text" value={e.event_subtype_other || ""}
                                onChange={(ev) => updEvent(i, { event_subtype_other: ev.target.value })}
                                placeholder="Descrivi il sottotipo"
                                className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                                data-testid={`event-${i}-subtype-other`} />
                            )}
                          </div>
                        )}

                        {/* Riga 3: titolo breve + luogo */}
                        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-2">
                          <input type="text" value={e.title || ""}
                            onChange={(ev) => updEvent(i, { title: ev.target.value })}
                            placeholder="Titolo breve (es. Produzione del contratto in giudizio)"
                            className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                            data-testid={`event-${i}-title`} />
                          <input type="text" value={e.location || ""}
                            onChange={(ev) => updEvent(i, { location: ev.target.value })}
                            placeholder="Luogo (facoltativo)"
                            className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                            data-testid={`event-${i}-location`} />
                        </div>

                        {/* Riga 4: descrizione libera */}
                        <textarea value={e.description || ""}
                          onChange={(ev) => updEvent(i, { description: ev.target.value })}
                          placeholder="Descrizione dell'evento"
                          rows={2}
                          className="w-full border hairline px-3 py-2 rounded-sm bg-white text-sm resize-y"
                          data-testid={`event-${i}-description`} />

                        {/* Warning sanitario */}
                        {showSanitaryWarning && (
                          <div className="border-2 border-forensic-amber bg-forensic-amber/10 p-3 flex items-start gap-2 text-xs" data-testid={`event-${i}-sanitary-warning`}>
                            <Info size={14} className="text-forensic-amber shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <b>Questo evento sembra riguardare la documentazione sanitaria.</b>{" "}
                              Per una migliore ricostruzione cronologica e una corretta analisi degli alert temporali,
                              valuta se inserirlo nella sezione <b>"Documentazione sanitaria"</b> anziché nella cronologia generale.
                            </div>
                            <button type="button"
                              onClick={() => updEvent(i, { _sanitary_dismissed: true })}
                              className="text-forensic-amber hover:underline shrink-0"
                              data-testid={`event-${i}-sanitary-dismiss`}>
                              Ignora
                            </button>
                          </div>
                        )}

                        {/* Riga 5: precisione data + data/testo */}
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-2">
                          <Select
                            value={e.date_precision || "exact"}
                            onChange={(v) => updEvent(i, { date_precision: v, ...(v === "exact" ? { date_text: "", date_year: "", date_month: "" } : { date: "" }) })}
                            options={DATE_PRECISION_OPTIONS}
                            testid={`event-${i}-date-precision`}
                            hideEmpty
                          />
                          {(e.date_precision || "exact") === "exact" ? (
                            <input type="date" min="1800-01-01" max="2200-12-31" value={e.date || ""}
                              onChange={(ev) => updEvent(i, { date: ev.target.value })}
                              className="border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                              data-testid={`event-${i}-date`} />
                          ) : e.date_precision === "unknown" ? (
                            <input type="text" value={e.date_text || "Data non nota"} disabled
                              className="border hairline px-3 py-2 rounded-sm bg-slate-100 text-sm text-slate-500"
                              data-testid={`event-${i}-date-text`} />
                          ) : (
                            <input type="text" value={e.date_text || ""}
                              onChange={(ev) => updEvent(i, { date_text: ev.target.value })}
                              placeholder={e.date_precision === "month_year" ? "es. Giugno 2023" : e.date_precision === "year_only" ? "es. 2020" : "es. 2020 circa"}
                              className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                              data-testid={`event-${i}-date-text`} />
                          )}
                          {(e.date_precision === "month_year" || e.date_precision === "year_only" || e.date_precision === "estimated") && (
                            <div className="flex gap-1">
                              <input type="number" min="1900" max="2100" value={e.date_year || ""}
                                onChange={(ev) => updEvent(i, { date_year: ev.target.value })}
                                placeholder="Anno"
                                className="w-24 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                                data-testid={`event-${i}-date-year`} />
                              {e.date_precision === "month_year" && (
                                <input type="number" min="1" max="12" value={e.date_month || ""}
                                  onChange={(ev) => updEvent(i, { date_month: ev.target.value })}
                                  placeholder="Mese"
                                  className="w-20 border hairline px-2 py-2 rounded-sm bg-white text-sm font-mono"
                                  data-testid={`event-${i}-date-month`} />
                              )}
                            </div>
                          )}
                        </div>

                        {/* Riga 6: fonte + note */}
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-2">
                          <Select
                            value={e.source || ""}
                            onChange={(v) => updEvent(i, { source: v })}
                            options={EVENT_SOURCES}
                            testid={`event-${i}-source`}
                          />
                          <input type="text" value={e.notes || ""}
                            onChange={(ev) => updEvent(i, { notes: ev.target.value })}
                            placeholder="Note (facoltativo)"
                            className="border hairline px-3 py-2 rounded-sm bg-white text-sm"
                            data-testid={`event-${i}-notes`} />
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              <button onClick={addEvent} className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-4 py-2 rounded-sm text-sm transition-colors" data-testid="add-event-btn">
                <Plus size={16} /> Aggiungi evento
              </button>
              {timeline.length === 0 && (
                <div className="border-l-2 border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-600 leading-snug">
                  Anche una cronologia essenziale (data del documento, produzione in giudizio, termini processuali) migliora sensibilmente l'inquadramento del fascicolo.
                </div>
              )}
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Strategia documentale</h2>
                <p className="text-sm text-slate-600 mt-2">
                  {role === "avvocato"
                    ? "Compila i campi processuali rilevanti; il sistema segnalerà i nodi tecnici da approfondire (non fornisce pareri legali)."
                    : "Facoltativo. Se non conosci alcuni campi puoi lasciarli vuoti — il consulente li chiarirà con te."}
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Parte producente il documento">
                  <input type="text" value={strategy.producing_party} onChange={(e) => setStrategy({ ...strategy, producing_party: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="strat-producing" />
                </Field>
                <Field label="Parte contro cui è utilizzato">
                  <input type="text" value={strategy.against_party} onChange={(e) => setStrategy({ ...strategy, against_party: e.target.value })} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="strat-against" />
                </Field>
                <Field label="Stato della sottoscrizione">
                  <Select value={strategy.signature_status} onChange={(v) => setStrategy({ ...strategy, signature_status: v })} options={[
                    { v: "riconosciuta", l: "Riconosciuta" }, { v: "disconosciuta", l: "Disconosciuta" }, { v: "non_conosciuta", l: "Non conosciuta" },
                  ]} testid="strat-signature-status" />
                </Field>
                <Field label="Termini processuali rilevanti">
                  <input type="text" value={strategy.deadline_notes} onChange={(e) => setStrategy({ ...strategy, deadline_notes: e.target.value })} placeholder="Es. udienza 20/03, osservazioni entro..." className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid="strat-deadline" />
                </Field>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <BoolField label="Istanza di verificazione proposta?" value={strategy.verificazione_requested} onChange={(v) => setStrategy({ ...strategy, verificazione_requested: v })} testid="strat-verificazione" />
                <BoolField label="Querela di falso prospettata?" value={strategy.querela_falso} onChange={(v) => setStrategy({ ...strategy, querela_falso: v })} testid="strat-querela" />
                <BoolField label="CTU già disposta?" value={strategy.ctu_disposed} onChange={(v) => setStrategy({ ...strategy, ctu_disposed: v })} testid="strat-ctu" />
              </div>

              <div className="border-t hairline pt-6 mt-4" data-testid="disconoscimento-section">
                <div className="overline mb-3">
                  {(() => {
                    const t = (ident.document_type || "").toLowerCase();
                    if (t.includes("testamento")) return "Dichiarazione di chi disconosce o contesta il testamento";
                    if (t.includes("scrittura") || t.includes("manoscritt") || t.includes("grafia")) return "Dichiarazione di chi disconosce o contesta la scrittura";
                    return "Dichiarazione di chi disconosce o contesta la sottoscrizione, il testamento o la scrittura";
                  })()}
                </div>
                <p className="text-sm text-slate-600 mb-3">
                  Il soggetto che disconosce o contesta il documento dichiara che (seleziona una sola opzione):
                </p>
                <div className="space-y-2">
                  {[
                    ["non_propria", "La firma non è la propria"],
                    ["non_ricorda", "Non ricordo di aver firmato"],
                    ["assomiglia", "Assomiglia alla propria"],
                    ["simile_ma_sicuro", "È molto simile/uguale alla propria ma sono sicuro di non aver firmato"],
                    ["non_riconosce_propria", "Non riconosce la scrittura come propria"],
                    ["non_riconosce_soggetto", "Non riconosce la scrittura come appartenente al soggetto indicato"],
                    ["grafia_anonima", "La grafia è anonima"],
                    ["testamento_non_testatore", "La grafia del testamento non corrisponde a quella del testatore"],
                    ["testo_non_soggetto", "La grafia del testo non corrisponde a quella del soggetto indicato"],
                    ["altro", "Altro (specifica nelle note)"],
                    ["non_so", "Non sa rispondere"],
                  ].map(([v, l]) => (
                    <label key={v}
                      className={`flex items-start gap-3 border hairline p-3 cursor-pointer transition-colors ${strategy.disconoscimento_dichiarazione === v ? "border-navy bg-navy/5" : "hover:border-slate-400 bg-white"}`}
                      data-testid={`disc-${v}`}>
                      <input type="radio" name="disconoscimento" value={v}
                        checked={strategy.disconoscimento_dichiarazione === v}
                        onChange={() => setStrategy({ ...strategy, disconoscimento_dichiarazione: v })}
                        className="mt-0.5 accent-navy" />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-3">
                  <Field label="Note aggiuntive alla dichiarazione (facoltativo)">
                    <input type="text" value={strategy.disconoscimento_note}
                      onChange={(e) => setStrategy({ ...strategy, disconoscimento_note: e.target.value })}
                      className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                      data-testid="disc-note" placeholder="Contesto, circostanze, ulteriori precisazioni..." />
                  </Field>
                </div>
                <p className="text-[11px] text-slate-500 italic mt-2 leading-snug">
                  Le dichiarazioni hanno esclusivamente funzione descrittiva. Non influiscono sul punteggio
                  né suggeriscono automaticamente autenticità o falsità.
                </p>
              </div>

              {/* SEZIONE CLINICA FACOLTATIVA */}
              <div className="border-t hairline pt-6 mt-4" data-testid="clinical-section">
                <div className="overline mb-2">Cartella clinica e condizioni dello scrivente <span className="text-slate-400 lowercase text-[9px]">(facoltativa)</span></div>
                <p className="text-xs text-slate-500 mb-3">
                  Questa sezione considera esclusivamente la <b>disponibilità</b> della documentazione sanitaria.
                  Non esprime alcun giudizio medico-legale né sulla capacità di intendere e di volere.
                </p>

                <div className="mb-4">
                  <div className="overline mb-2">Condizioni note dichiarate</div>
                  <div className="flex flex-wrap gap-2">
                    {CLINICAL_CONDITIONS.map((cond) => {
                      const selected = clinical.conditions.includes(cond);
                      return (
                        <button key={cond} type="button" data-testid={`cond-${cond}`}
                          onClick={() => setClinical({
                            ...clinical,
                            conditions: selected
                              ? clinical.conditions.filter((c) => c !== cond)
                              : [...clinical.conditions, cond],
                          })}
                          className={`text-xs px-3 py-1 rounded-sm border transition-colors ${selected ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"}`}>
                          {cond}
                        </button>
                      );
                    })}
                    {(() => {
                      const otherEntry = clinical.conditions.find((c) => typeof c === "string" && c.startsWith("Altro:"));
                      const otherSelected = otherEntry !== undefined;
                      return (
                        <button type="button" data-testid="cond-altro"
                          onClick={() => setClinical({
                            ...clinical,
                            conditions: otherSelected
                              ? clinical.conditions.filter((c) => !(typeof c === "string" && c.startsWith("Altro:")))
                              : [...clinical.conditions, "Altro: "],
                          })}
                          className={`text-xs px-3 py-1 rounded-sm border transition-colors ${otherSelected ? "bg-navy text-white border-navy" : "bg-white hairline hover:border-navy"}`}>
                          Altro
                        </button>
                      );
                    })()}
                  </div>
                  {(() => {
                    const otherEntry = clinical.conditions.find((c) => typeof c === "string" && c.startsWith("Altro:"));
                    if (otherEntry === undefined) return null;
                    const currentText = otherEntry.replace(/^Altro:\s*/, "");
                    return (
                      <div className="mt-3">
                        <Field label="Specifica altra condizione">
                          <input type="text" value={currentText}
                            onChange={(e) => setClinical({
                              ...clinical,
                              conditions: clinical.conditions.map((c) =>
                                (typeof c === "string" && c.startsWith("Altro:")) ? `Altro: ${e.target.value}` : c
                              ),
                            })}
                            placeholder="Es. neuropatia periferica, decadimento cognitivo lieve..."
                            className="w-full border hairline px-3 py-2 rounded-sm bg-white"
                            data-testid="cond-altro-input" />
                        </Field>
                      </div>
                    );
                  })()}
                </div>

                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline">Documentazione sanitaria dichiarata</div>
                    <button type="button" onClick={() => setClinical({
                      ...clinical,
                      documents: [...clinical.documents, { id: _mkId("clindoc"), type: "", type_label: "", type_other: "", title: "", event_description: "", outcome: "", date: "", date_precision: "unknown", date_text: "", date_year: "", date_month: "", notes: "" }],
                    })} className="text-xs text-navy hover:underline flex items-center gap-1" data-testid="add-clinical-doc">
                      <Plus size={11} /> Aggiungi documento
                    </button>
                  </div>
                  {clinical.documents.length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Nessun documento sanitario dichiarato.</div>
                  ) : (
                    <div className="space-y-3">
                      {clinical.documents.map((d, i) => {
                        const upd = (patch) => setClinical({ ...clinical, documents: clinical.documents.map((x, k) => k === i ? { ...x, ...patch } : x) });
                        return (
                          <div key={d.id} className="border hairline p-3 bg-paper space-y-2" data-testid={`clinical-doc-${i}`}>
                            {/* Riga 1: precisione + data/testo + tipo + rimuovi */}
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`clinical-doc-${i}-date-block`}>
                              <Select value={d.date_precision || (d.date ? "exact" : "unknown")}
                                onChange={(v) => upd({ date_precision: v, ...(v === "exact" ? { date_text: "", date_year: "", date_month: "" } : { date: "" }) })}
                                options={DATE_PRECISION_OPTIONS} testid={`clinical-doc-${i}-date-precision`} hideEmpty />
                              {(d.date_precision || (d.date ? "exact" : "unknown")) === "exact" ? (
                                <input type="date" min="1800-01-01" max="2200-12-31" value={d.date || ""}
                                  onChange={(e) => upd({ date: e.target.value })}
                                  className="border hairline px-2 py-1.5 rounded-sm text-xs font-mono bg-white"
                                  data-testid={`clinical-doc-${i}-date`} />
                              ) : d.date_precision === "unknown" ? (
                                <input type="text" value="Data non nota" disabled
                                  className="border hairline px-3 py-1.5 rounded-sm bg-slate-100 text-xs text-slate-500"
                                  data-testid={`clinical-doc-${i}-date-text`} />
                              ) : (
                                <input type="text" value={d.date_text || ""}
                                  onChange={(e) => upd({ date_text: e.target.value })}
                                  placeholder={d.date_precision === "month_year" ? "es. Aprile 2025" : d.date_precision === "year_only" ? "es. 2025" : "es. circa 2025"}
                                  className="border hairline px-3 py-1.5 rounded-sm bg-white text-xs"
                                  data-testid={`clinical-doc-${i}-date-text`} />
                              )}
                              <select value={d.type || ""}
                                onChange={(e) => {
                                  const opt = CLINICAL_DOC_TYPES.find((t) => t.v === e.target.value);
                                  upd({ type: e.target.value, type_label: opt?.l || "", ...(e.target.value !== "altro" ? { type_other: "" } : {}) });
                                }}
                                className="border hairline px-2 py-1.5 rounded-sm text-sm bg-white"
                                data-testid={`clinical-doc-${i}-type`}>
                                <option value="">Tipo documento…</option>
                                {CLINICAL_DOC_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                              </select>
                              <button type="button" onClick={() => setClinical({ ...clinical, documents: clinical.documents.filter((_, k) => k !== i) })}
                                className="text-slate-500 hover:text-forensic-red p-1"><Trash2 size={14} /></button>
                            </div>
                            {(d.date_precision === "month_year" || d.date_precision === "year_only" || d.date_precision === "estimated") && (
                              <div className="flex gap-1">
                                <input type="number" min="1800" max="2200" value={d.date_year || ""}
                                  onChange={(e) => upd({ date_year: e.target.value })}
                                  placeholder="Anno"
                                  className="w-24 border hairline px-2 py-1.5 rounded-sm bg-white text-xs font-mono"
                                  data-testid={`clinical-doc-${i}-date-year`} />
                                {d.date_precision === "month_year" && (
                                  <input type="number" min="1" max="12" value={d.date_month || ""}
                                    onChange={(e) => upd({ date_month: e.target.value })}
                                    placeholder="Mese"
                                    className="w-20 border hairline px-2 py-1.5 rounded-sm bg-white text-xs font-mono"
                                    data-testid={`clinical-doc-${i}-date-month`} />
                                )}
                              </div>
                            )}

                            {/* Riga 1b: campo libero se tipo=altro */}
                            {d.type === "altro" && (
                              <input type="text" value={d.type_other || ""}
                                onChange={(e) => upd({ type_other: e.target.value })}
                                placeholder="Descrivi il tipo di documentazione"
                                className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                                data-testid={`clinical-doc-${i}-type-other`} />
                            )}

                            {/* Riga 2: titolo/oggetto */}
                            <input type="text" value={d.title || ""}
                              onChange={(e) => upd({ title: e.target.value })}
                              placeholder="Titolo o oggetto del documento (es. Controllo ortopedico)"
                              className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`clinical-doc-${i}-title`} />

                            {/* Riga 3: evento descritto */}
                            <input type="text" value={d.event_description || ""}
                              onChange={(e) => upd({ event_description: e.target.value })}
                              placeholder="Evento o condizione descritta (es. Rimozione del tutore)"
                              className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`clinical-doc-${i}-event`} />

                            {/* Riga 4: esito */}
                            <input type="text" value={d.outcome || ""}
                              onChange={(e) => upd({ outcome: e.target.value })}
                              placeholder="Esito, indicazioni o conseguenze (es. Recupero funzionale)"
                              className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`clinical-doc-${i}-outcome`} />

                            {/* Riga 5: note */}
                            <input type="text" value={d.notes || ""}
                              onChange={(e) => upd({ notes: e.target.value })}
                              placeholder="Note aggiuntive (facoltativo)"
                              className="w-full border hairline px-3 py-2 rounded-sm text-sm bg-white"
                              data-testid={`clinical-doc-${i}-notes`} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="overline">Ricoveri</div>
                    <button type="button" onClick={() => setClinical({
                      ...clinical,
                      ricoveri: [...clinical.ricoveri, { id: _mkId("ricov"), start_date: "", end_date: "", structure: "", documentation_available: false }],
                    })} className="text-xs text-navy hover:underline flex items-center gap-1" data-testid="add-ricovero">
                      <Plus size={11} /> Aggiungi ricovero
                    </button>
                  </div>
                  {clinical.ricoveri.length === 0 ? (
                    <div className="text-xs text-slate-500 italic">Nessun ricovero dichiarato.</div>
                  ) : (
                    <div className="space-y-2">
                      {clinical.ricoveri.map((r, i) => (
                        <div key={r.id} className="border hairline p-3 grid grid-cols-1 md:grid-cols-[7rem_7rem_1fr_auto_auto] gap-2 items-center bg-paper">
                          <input type="date" min="1800-01-01" max="2200-12-31" value={r.start_date || ""}
                            onChange={(e) => setClinical({ ...clinical, ricoveri: clinical.ricoveri.map((x, k) => k === i ? { ...x, start_date: e.target.value } : x) })}
                            className="border hairline px-2 py-1.5 rounded-sm text-xs font-mono bg-white" placeholder="Da" />
                          <input type="date" min="1800-01-01" max="2200-12-31" value={r.end_date || ""}
                            onChange={(e) => setClinical({ ...clinical, ricoveri: clinical.ricoveri.map((x, k) => k === i ? { ...x, end_date: e.target.value } : x) })}
                            className="border hairline px-2 py-1.5 rounded-sm text-xs font-mono bg-white" placeholder="A" />
                          <input type="text" value={r.structure || ""}
                            onChange={(e) => setClinical({ ...clinical, ricoveri: clinical.ricoveri.map((x, k) => k === i ? { ...x, structure: e.target.value } : x) })}
                            className="border hairline px-2 py-1.5 rounded-sm text-sm bg-white" placeholder="Struttura" />
                          <label className="text-xs inline-flex items-center gap-1 whitespace-nowrap">
                            <input type="checkbox" checked={!!r.documentation_available}
                              onChange={(e) => setClinical({ ...clinical, ricoveri: clinical.ricoveri.map((x, k) => k === i ? { ...x, documentation_available: e.target.checked } : x) })}
                              className="accent-navy" />
                            Docs disp.
                          </label>
                          <button type="button" onClick={() => setClinical({ ...clinical, ricoveri: clinical.ricoveri.filter((_, k) => k !== i) })}
                            className="text-slate-500 hover:text-forensic-red p-1"><Trash2 size={14} /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* CONSENSO ART. 9 GDPR — solo se sono presenti dati sanitari */}
              {hasClinicalData && (
                <div className={`border-2 p-4 mt-2 ${art9Consent ? "border-forensic-green bg-forensic-green/5" : "border-forensic-red bg-forensic-red/5"}`} data-testid="art9-consent-block">
                  <div className="overline mb-2 text-forensic-red flex items-center gap-1">
                    <Info size={12} /> Consenso obbligatorio per dati particolari (art. 9 GDPR)
                  </div>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" checked={art9Consent}
                      onChange={(e) => setArt9Consent(e.target.checked)}
                      className="mt-1 accent-navy w-4 h-4 shrink-0"
                      data-testid="art9-consent-checkbox" />
                    <span className="text-sm text-slate-700 leading-relaxed">
                      Ho preso visione dell'<a href="https://www.periziafirma.it/privacy-policy/"
                        target="_blank" rel="noreferrer" className="text-navy underline font-medium"
                        data-testid="art9-privacy-link">informativa privacy</a> ed <b>autorizzo espressamente
                      il trattamento dei dati particolari</b> (relativi alla salute, alle condizioni cliniche,
                      ai ricoveri e alla documentazione sanitaria) <b>ex art. 9 GDPR</b>, limitatamente alle
                      finalità di valutazione documentale preliminare del fascicolo grafologico.
                      Senza questo consenso non è possibile proseguire con i dati sanitari inseriti.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6">
              <div>
                <h2 className="font-serif text-2xl font-semibold">Riepilogo e generazione fascicolo</h2>
                <p className="text-sm text-slate-600 mt-2">
                  Verifica quanto inserito. Nessun dato di contatto è richiesto per generare il report:
                  potrai decidere in seguito se richiedere una consulenza gratuita o acquistare il fascicolo Premium.
                </p>
              </div>

              {/* MULTIDOC WIZARD SUMMARY — B.3 */}
              <WizardMultidocPreview questionedDocuments={questionedDocuments} caseId={null} />

              {/* Summary review */}
              <div className="border hairline p-4 bg-paper" data-testid="wizard-review">
                <div className="overline mb-3">Riepilogo</div>
                <dl className="grid md:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <Row label="Percorso" v={role === "avvocato" ? "Avvocato" : role === "privato" ? "Cliente privato" : "Altro professionista"} />
                  <Row label="Richiedente" v={[requester.title, requester.first_name, requester.last_name].filter(Boolean).join(" ") || "—"} />
                  {requester.organization && <Row label="Studio / Organizzazione" v={requester.organization} />}
                  <Row label="E-mail richiedente" v={requester.email || "—"} />
                  {(assistedParty.first_name || assistedParty.last_name) && (
                    <Row label="Parte assistita" v={[assistedParty.first_name, assistedParty.last_name].filter(Boolean).join(" ")} />
                  )}
                  {assistedParty.role && (
                    <Row label="Posizione processuale" v={assistedParty.role === "altro" ? (assistedParty.role_other || "Altro") : (ASSISTED_PARTY_ROLES.find(r => r.v === assistedParty.role)?.l || PRIVATE_CASE_ROLES.find(r => r.v === assistedParty.role)?.l || assistedParty.role)} />
                  )}
                  {(ident.subject?.first_name || ident.subject?.last_name || ident.subject?.name) && (
                    <Row label="Soggetto scrivente apparente" v={[ident.subject?.first_name, ident.subject?.last_name].filter(Boolean).join(" ") || ident.subject?.name} />
                  )}
                  <Row label="Tipo documento" v={ident.document_type || "—"} />
                  <Row label="Disponibilità" v={ident.document_availability || "—"} />
                  <Row label="Fase" v={ident.phase || "—"} />
                  <Row label="Data doc. in verifica" v={ident.contested_document_date || "—"} />
                  <Row label="Comparative dichiarate" v={String(comps.comparatives.length)} />
                  <Row label="Eventi timeline" v={String(timeline.length)} />
                  <Row label="Documenti sanitari" v={String((clinical.documents || []).length)} />
                  <Row label="Stato sottoscrizione" v={strategy.signature_status || "—"} />
                  <Row label="CTU / Querela / Verificazione" v={[
                    strategy.ctu_disposed ? "CTU" : null,
                    strategy.querela_falso ? "Querela" : null,
                    strategy.verificazione_requested ? "Verificazione" : null,
                  ].filter(Boolean).join(", ") || "—"} />
                </dl>
                {caseShortDescription && (
                  <div className="mt-4 pt-3 border-t hairline">
                    <div className="overline mb-1">Breve descrizione del caso</div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed" data-testid="review-case-description">{caseShortDescription}</p>
                  </div>
                )}
              </div>

              {/* Consenso privacy obbligatorio */}
              <div className={`border-2 p-4 mt-2 ${privacyConsent ? "border-forensic-green bg-forensic-green/5" : "border-forensic-red bg-forensic-red/5"}`} data-testid="privacy-consent-block">
                <label className="flex gap-3 items-start cursor-pointer">
                  <input type="checkbox" checked={privacyConsent}
                    onChange={(e) => setPrivacyConsent(e.target.checked)}
                    className="mt-1 accent-navy w-4 h-4 shrink-0"
                    data-testid="privacy-consent-checkbox" required />
                  <span className="text-sm text-slate-700 leading-relaxed">
                    <b>Presa visione dell'informativa</b> ai sensi del Regolamento UE 2016/679 (GDPR),
                    <b> acconsento al trattamento dei miei dati personali</b> per le finalità connesse alla
                    generazione del fascicolo di valutazione preliminare. Il consenso è obbligatorio per procedere.
                    <br />
                    <a href="https://www.periziafirma.it/privacy-policy/" target="_blank" rel="noreferrer"
                       className="text-navy underline hover:text-navy-dark font-medium mt-2 inline-block"
                       data-testid="wizard-privacy-link">
                      Leggi l'informativa privacy completa →
                    </a>
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t hairline flex items-center justify-between">
            <button onClick={prev} disabled={step === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-sm border hairline hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="wizard-prev">
              <ArrowLeft size={16} /> Indietro
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors" data-testid="wizard-next">
                Continua <ArrowRight size={16} />
              </button>
            ) : (
              <button onClick={submit} disabled={saving || !privacyConsent} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-dark text-white px-6 py-2 rounded-sm text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="wizard-submit"
                title={!privacyConsent ? "Devi prima accettare l'informativa privacy" : ""}>
                {saving ? "Salvataggio..." : "Genera fascicolo"} <ArrowRight size={16} />
              </button>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex justify-between border-b hairline py-1.5">
      <dt className="text-slate-500 uppercase text-[10px] tracking-widest">{label}</dt>
      <dd className="font-medium text-slate-800">{v}</dd>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="overline mb-2">{label}</div>
      {children}
    </label>
  );
}

function Select({ value, onChange, options, testid, hideEmpty }) {
  return (
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border hairline px-3 py-2 rounded-sm bg-white" data-testid={testid}>
      {!hideEmpty && <option value="">— Seleziona —</option>}
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function BoolField({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <div className="flex gap-2">
        {[[true, "Sì"], [false, "No"], [null, "?"]].map(([v, l]) => (
          <button key={String(v)} onClick={() => onChange(v)} data-testid={`${testid}-${String(v)}`}
            className={`px-4 py-2 rounded-sm text-sm border hairline transition-colors ${value === v ? "bg-navy text-white border-navy" : "bg-white hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

function Chk({ label, checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="accent-navy" data-testid={testid} />
      <span className="text-sm">{label}</span>
    </label>
  );
}
